import React from "react";
import { TESTIMONIALS } from "../data/site";
import { Star } from "lucide-react";

/**
 * Trusted By / Leading Creators — testimonials grid.
 * Typography rules:
 *   • "Trusted By" pill       → Karvest (brand accent), slightly larger on desktop
 *   • "Leading Creators" head → Clash Display Medium 500 (gradient)
 *   • Subheading paragraph    → Karvest Regular
 *   • Creator names           → SF Pro Display Medium (500)
 *   • Subscriber counts       → SF Pro Display Regular (400)
 *   • Testimonial body text   → SF Pro Display Regular (400)
 */
export default function Testimonials() {
  return (
    <section id="testimonials" className="relative py-6 md:py-14">
      <div className="max-w-6xl mx-auto px-5 md:px-8 text-center">
        {/* "Trusted By" pill — Karvest, slightly larger on desktop */}
        <div className="reveal">
          <span
            className="inline-flex items-center rounded-full"
            style={{
              padding: "12px 30px",
              border: "1px solid rgba(139,92,246,0.35)",
              backgroundColor: "rgba(15,23,42,0.6)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              color: "#E5E7EB",
              fontFamily: "'Karvest',sans-serif",
              fontWeight: 400,
              fontSize: "clamp(15px, 1.5vw, 22px)",
              letterSpacing: "0.01em",
              lineHeight: 1.1,
            }}
          >
            Trusted By
          </span>
        </div>

        {/* "Leading Creators" heading */}
        <h2
          className="reveal text-transparent bg-clip-text mt-4"
          style={{
            fontFamily: "'Clash Display',sans-serif",
            fontWeight: 500,
            fontSize: "clamp(44px, 8vw, 96px)",
            lineHeight: 0.95,
            letterSpacing: "-0.02em",
            backgroundImage:
              "linear-gradient(180deg,#EDE9FE 0%,#C4B5FD 50%,#8B5CF6 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            margin: 0,
          }}
        >
          Leading Creators
        </h2>

        {/* Subheading — Karvest Regular */}
        <p
          className="reveal mt-3"
          style={{
            fontFamily: "'Karvest',sans-serif",
            fontWeight: 400,
            fontSize: "clamp(13px, 1vw, 17px)",
            lineHeight: 1.5,
            letterSpacing: "0.005em",
            color: "rgba(255,255,255,0.72)",
          }}
        >
          The testimonials below are from creators who have
          <br />
          worked directly with Clickory founder, Sohan Bin Ashik
        </p>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {TESTIMONIALS.map((t) => (
            <div
              key={t.name}
              className="reveal relative glass-card p-6 md:p-8 text-left overflow-hidden"
            >
              {/* Top-right purple shine */}
              <div
                aria-hidden
                className="pointer-events-none absolute -top-16 -right-10 w-56 h-56 rounded-full blur-3xl opacity-40"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(139,92,246,0.55), transparent)",
                }}
              />
              <div className="relative flex items-center gap-4">
                <img
                  src={t.img}
                  alt={t.name}
                  className="w-14 h-14 rounded-full object-cover ring-2 ring-white/10"
                />
                <div>
                  {/* Creator name — SF Pro Display Medium */}
                  <div
                    className="text-white"
                    style={{
                      fontFamily: "'SF Pro Display',sans-serif",
                      fontWeight: 500,
                      fontSize: "clamp(17px, 1.35vw, 22px)",
                      letterSpacing: "-0.005em",
                      lineHeight: 1.2,
                    }}
                  >
                    {t.name}
                  </div>
                  {/* Subscriber count — SF Pro Display Regular */}
                  <div
                    style={{
                      fontFamily: "'SF Pro Display',sans-serif",
                      fontWeight: 400,
                      fontSize: "clamp(13px, 0.95vw, 15px)",
                      color: "rgba(255,255,255,0.62)",
                      letterSpacing: "-0.003em",
                      lineHeight: 1.3,
                      marginTop: 2,
                    }}
                  >
                    {t.subs}
                  </div>
                </div>
              </div>
              <div className="relative flex gap-1 mt-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-[#F5C518] text-[#F5C518]"
                  />
                ))}
              </div>
              {/* Testimonial body — SF Pro Display Regular */}
              <p
                className="relative mt-4"
                style={{
                  fontFamily: "'SF Pro Display',sans-serif",
                  fontWeight: 400,
                  fontSize: "clamp(14px, 1.05vw, 16px)",
                  lineHeight: 1.55,
                  color: "rgba(255,255,255,0.85)",
                }}
              >
                &ldquo;{t.text}&rdquo;
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
